//src/utils/type-guards.ts

import type { APIError } from '@/types/api.types';
import type { FieldErrors } from '@/types/AuthState';

/**
 * Shared type guards for runtime type checking across the application.
 * Organized by domain/category for easy navigation.
 */

/* ─── API & Error Guards ─── */

/**
 * Type guard to check if an error is an APIError
 */
export function isAPIError(error: unknown): error is APIError {
  return (
    typeof error === 'object' &&
    error !== null &&
    ('fieldErrors' in error || 'message' in error)
  );
}

/**
 * Type guard to check if an object is FieldErrors
 */
export function isFieldErrors(obj: unknown): obj is FieldErrors {
  if (typeof obj !== 'object' || obj === null) return false;
  return Object.values(obj).every((v) => typeof v === 'string' || typeof v === 'undefined');
}

/* ─── Array & Collection Guards ─── */

/**
 * Type guard to check if value is a number array
 */
export function isNumberArray(value: unknown): value is number[] {
  return Array.isArray(value) && value.every(item => typeof item === 'number');
}

/**
 * Type guard to check if value is a string array
 */
export function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(item => typeof item === 'string');
}

/* ─── Object Shape Guards ─── */

/**
 * Type guard for objects with serialize method
 */
interface SerializableRecord {
  serialize: () => number[];
}

export function isSerializable(obj: unknown): obj is SerializableRecord {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'serialize' in obj &&
    typeof (obj as { serialize: unknown }).serialize === 'function'
  );
}

/**
 * Type guard to check if value is a plain object (not array, null, etc.)
 */
export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.prototype.toString.call(value) === '[object Object]'
  );
}

/* ─── Primitive Guards ─── */

const EMPTY_LENGTH = 0;

/**
 * Type guard to check if value is a non-empty string
 */
export function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > EMPTY_LENGTH;
}

/**
 * Type guard to check if value is a valid number (not NaN)
 */
export function isValidNumber(value: unknown): value is number {
  return typeof value === 'number' && !Number.isNaN(value);
}
